import { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Tooltip,
  Stack,
  IconButton,
  Button,
  Box,
  TextField,
} from "@mui/material";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ThumbsUpIcon from "../../../assets/ThumbsUp.svg";
import ThumbsDownIcon from "../../../assets/ThumbsDown.svg";
import { useTheme } from "@mui/material/styles";
import {
  CaretDown,
  XCircle,
} from "@phosphor-icons/react";
import ButterflySVG from "../../../assets/an_butterfly_green.svg";

const AISummarySection = ({ title, content, expanded = true, addSummary, entityName, onToggle, onAiSummaryChange }) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(content);
  const [feedbackThumbsFocus, setFeedbackThumbsFocus] = useState(null);
  const [lastActionTaken, setLastActionTaken] = useState(null); // "accepted", "edited", "denied"

  useEffect(() => {
    if (onAiSummaryChange) {
      if (lastActionTaken === null && feedbackThumbsFocus === null) { return; }
      onAiSummaryChange({
        lastActionTaken,
        feedbackThumbsFocus,
      });
    }
  }, [lastActionTaken, feedbackThumbsFocus, onAiSummaryChange]);

  useEffect(() => {
    if (!isEditing) {
      setEditedContent(content);
    }
  }, [content, isEditing]);

  const addSummaryContent = () => {
    if (isEditing) {
      addSummary(editedContent);
      setIsEditing(false);
      setLastActionTaken("edited");
    } else {
      addSummary(content);
      setLastActionTaken("accepted");
    }
  }
  const onDeny = () => {
    onToggle();
    setLastActionTaken("denied");
  }

  const markdownComponents = {
    // disallow images and links from AI-output for saftey concerns:
    img: () => null,
    a: ({ children }) => <>{children}</>,                    
    p: ({ children }) => (
      <Typography variant="body2" sx={{ lineHeight: 1.6, color: theme.palette.success.main, margin: 0 }}>
        {children}
      </Typography>
    ),
    h1: ({ children }) => <Typography variant="h5" sx={{ color: theme.palette.success.main }}>{children}</Typography>,
    h2: ({ children }) => <Typography variant="h6" sx={{ color: theme.palette.success.main }}>{children}</Typography>,
    h3: ({ children }) => <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.success.main }}>{children}</Typography>,
    h4: ({ children }) => <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.success.main }}>{children}</Typography>,
    li: ({ children }) => (
      <Typography component="li" variant="body2" sx={{ lineHeight: 1.6, color: theme.palette.success.main }}>
        {children}
      </Typography>
    ),
  };

  return (
    <Stack spacing={5} sx={{ width: "100%" }}>
      <Box
        sx={{
          borderRadius: "12px",
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          overflow: "hidden",
        }}
      >
        <Accordion
          expanded={expanded}
          onChange={onToggle}
          sx={{
            boxShadow: "none",
            "&:before": { display: "none" },
            border: "none",
            overflow: "hidden",
            backgroundColor: "transparent",
          }}
        >
          <AccordionSummary
            expandIcon={<CaretDown color={theme.palette.action.active} />}
            aria-controls={`${title}-content`}
            id={`${title}-header`}
            sx={{
              padding: "0 16px",
              minHeight: "40px",

              "& .MuiAccordionSummary-content": { margin: "12px 0" },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box component="img" src={ButterflySVG} alt="Butterfly logo" />
                <Typography variant="body1" sx={{ fontWeight: "400 !important" }}>
                  AI Summary
                </Typography>
              </Stack>
              <Tooltip
                title={
                  <Typography variant="body2">
                    This draft note is generated using transcript data, treatment plan goals,
                    appointment data — all working together to capture a complete picture of your
                    session.
                  </Typography>
                }
                arrow
              >
                <InfoOutlinedIcon
                  sx={{
                    fontSize: 16,
                    color: theme.palette.action.active,
                    cursor: "help",
                  }}
                />
              </Tooltip>
            </Stack>
          </AccordionSummary>

          <AccordionDetails
            sx={{
              padding: "16px 0",
              display: "flex",
              flexDirection: "column",
              gap: "16px"
            }}
          >
            {isEditing ? (
              <TextField
                multiline
                fullWidth
                minRows={1}
                maxRows={Infinity}
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: "12px",
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    padding: "16px 12px !important",
                    '& fieldset': {
                      borderColor: theme.palette.divider,
                      borderRadius: "12px",
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                  '& textarea': {
                    padding: "0 !important",
                  },
                }}
              />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "8px",
                  alignSelf: "stretch",
                  overflow: "hidden",
                  borderRadius: "12px",
                  backgroundColor: theme.palette.green[50],
                  padding: "12px",
                }}>
                {content && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.success.main,
                    }}
                  >
                    Proposed:
                  </Typography>
                )}
                <ReactMarkdown components={markdownComponents}>
                  {content || "No content available"}
                </ReactMarkdown>
              </Box>
            )}
            <Stack direction="row">
              <IconButton
                aria-label="thumbs up"
                onClick={() => setFeedbackThumbsFocus(feedbackThumbsFocus === 'up' ? null : 'up')}
                sx={{
                  backgroundColor: feedbackThumbsFocus === 'up' ? theme.palette.action.selected : 'transparent',
                  '&:hover': {
                    backgroundColor: feedbackThumbsFocus === 'up'
                      ? `${theme.palette.action.selected} !important`
                      : `${theme.palette.action.hover} !important`,
                  },
                }}
              >
                <img src={ThumbsUpIcon} alt="thumbs up" />
              </IconButton>

              <IconButton
                aria-label="thumbs down"
                onClick={() => setFeedbackThumbsFocus(feedbackThumbsFocus === 'down' ? null : 'down')}
                sx={{
                  backgroundColor: feedbackThumbsFocus === 'down' ? theme.palette.action.selected : 'transparent',
                  '&:hover': {
                    backgroundColor: feedbackThumbsFocus === 'down'
                      ? `${theme.palette.action.selected} !important`
                      : `${theme.palette.action.hover} !important`,
                  },
                }}
              >
                <img src={ThumbsDownIcon} alt="thumbs down" />
              </IconButton>
            </Stack>

            <Stack direction="row" spacing="8px">
              <Button
                onClick={addSummaryContent}
                size="small"
                variant="contained"
                sx={{
                  fontSize: 13,
                  backgroundColor: theme.palette.success.main,
                  '&:hover': {
                    backgroundColor: theme.palette.success.dark,
                  }
                }}
                startIcon={<CheckCircleOutlineIcon />}
              >
                Add To {entityName}
              </Button>
              {isEditing ? (
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  sx={{ fontSize: 13 }}
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </Button>
              ) : (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    sx={{ fontSize: 13 }}
                    startIcon={<CheckCircleOutlineIcon />}
                    onClick={() => setIsEditing(true)}
                  >
                    Add With Edits
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    color="error"
                    sx={{ fontSize: 13 }}
                    startIcon={<XCircle color={theme.palette.error.main} />}
                    onClick={onDeny}
                  >
                    Deny
                  </Button>
                </>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Stack >
  );
};

export default AISummarySection;
